<template>
  <div>
    <div v-if="loading" class="text-sm text-gray-500 py-4 text-center">
      {{ $t('Components.SlotPicker.loading', tLocale) }}
    </div>
    <div v-else-if="grouped.length === 0" class="py-6 text-center">
      <div class="text-sm text-gray-700">
        {{ $t('Components.SlotPicker.empty', tLocale) }}
      </div>
      <slot name="empty"></slot>
    </div>
    <div v-else class="space-y-4">
      <!-- Date selector: one chip per date that has available slots. Picking a date
           reveals only that date's slots below, so a sponsor with slots across many
           days no longer reads as a single long stack. -->
      <div
        class="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 snap-x"
        role="tablist"
      >
        <button
          v-for="group in grouped"
          :key="group.day"
          type="button"
          role="tab"
          :aria-selected="selectedDay === group.day"
          :class="[
            'shrink-0 snap-start w-16 py-2 rounded-2xl border flex flex-col items-center justify-center transition-all duration-200 min-h-[56px]',
            selectedDay === group.day
              ? 'btn-brand shadow-sm ring-4 ring-teal-50'
              : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-teal-500 hover:bg-teal-50/20'
          ]"
          @click="selectedDay = group.day"
        >
          <span
            class="text-[10px] font-bold uppercase tracking-wide leading-none"
          >
            {{ formatChipWeekday(group.iso) }}
          </span>
          <span class="text-lg font-black leading-none mt-1">
            {{ formatChipDay(group.iso) }}
          </span>
        </button>
      </div>

      <!-- Slots for the selected date. -->
      <div v-if="selectedGroup" class="space-y-2">
        <div
          class="text-xs font-semibold uppercase tracking-wide text-gray-500"
        >
          {{ formatDayHeading(selectedGroup.iso) }}
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button
            v-for="slot in selectedGroup.slots"
            :key="slot.id"
            type="button"
            :class="[
              'px-4 py-2.5 rounded-full text-xs font-bold transition-all duration-200 border text-center min-h-[44px] sm:min-h-[40px] flex items-center justify-center',
              selectedId === slot.id
                ? 'btn-brand shadow-sm ring-4 ring-teal-50'
                : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-teal-500 hover:bg-teal-50/20'
            ]"
            @click="$emit('select', slot)"
          >
            {{ formatRange(slot.startAt, slot.endAt) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatEventDateTime, eventDateKey } from '@/helpers/event-time';
import { intlLocale } from '@/i18n';

export default {
  name: 'SlotPicker',
  props: {
    slots: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    selectedId: { type: String, default: null },
    // Mirrors BookMeetingModal's prop — English inside the AI Matcher, event language in the lobby.
    english: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedDay: null
    };
  },
  computed: {
    tLocale() {
      return this.english ? 'en' : this.$i18n.locale;
    },
    // Intl tag for weekday/month names; undefined keeps the helper's English (en-GB) default.
    intlTag() {
      return this.tLocale === 'en' ? undefined : intlLocale();
    },
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    },
    grouped() {
      const map = new Map();
      this.slots.forEach((s) => {
        const day = eventDateKey(s.startAt, this.eventTz, this.eventMode);
        if (!map.has(day)) map.set(day, []);
        map.get(day).push(s);
      });
      return Array.from(map.entries())
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([day, slots]) => {
          const sorted = slots.sort((a, b) => (a.startAt < b.startAt ? -1 : 1));
          // Keep a representative ISO for the heading so it formats in the display zone (the `day`
          // key is a plain date string used only for grouping/sorting).
          return { day, iso: sorted[0] && sorted[0].startAt, slots: sorted };
        });
    },
    selectedGroup() {
      return this.grouped.find((g) => g.day === this.selectedDay) || null;
    }
  },
  watch: {
    // Default to the nearest date with slots, and re-anchor if a refetch drops the
    // currently-selected date (e.g. its last slot got booked by someone else).
    grouped: {
      immediate: true,
      handler(groups) {
        if (!groups.length) {
          this.selectedDay = null;
          return;
        }
        if (!groups.some((g) => g.day === this.selectedDay)) {
          this.selectedDay = groups[0].day;
        }
      }
    }
  },
  methods: {
    formatChipWeekday(iso) {
      return formatEventDateTime(
        iso,
        this.eventTz,
        this.eventMode,
        {
          weekday: 'short'
        },
        this.intlTag
      );
    },
    formatChipDay(iso) {
      return formatEventDateTime(
        iso,
        this.eventTz,
        this.eventMode,
        {
          day: 'numeric'
        },
        this.intlTag
      );
    },
    formatDayHeading(iso) {
      return formatEventDateTime(
        iso,
        this.eventTz,
        this.eventMode,
        {
          weekday: 'long',
          month: 'short',
          day: 'numeric'
        },
        this.intlTag
      );
    },
    formatRange(startIso, endIso) {
      const start = formatEventDateTime(
        startIso,
        this.eventTz,
        this.eventMode,
        {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        this.intlTag
      );
      const end = formatEventDateTime(
        endIso,
        this.eventTz,
        this.eventMode,
        {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        this.intlTag
      );
      return `${start} – ${end}`;
    }
  }
};
</script>
